import React, { useMemo, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import type { User, UserLoan } from '@matech/thebigpos-sdk'
import { generatePath, useNavigate } from 'react-router-dom'
import { useVenti } from 'venti'
import * as yup from 'yup'
import { useIntl } from 'react-intl'
import { useMutation, useQuery } from '@tanstack/react-query'
import { LoanCalculatorFormValues } from '@/pages/protected/loanCalculator/types'
import { LoanPurposeTypes } from '@/pages/protected/loanCalculator/constants'
import {
	Form,
	FormRow,
	TextField,
} from '../../../../components/formControls'
import { Button, LoadingBtn } from '../../../../components/Button'
import useForm from '../../../../hooks/useForm'
import { eventTypes } from '../../../../services/constants'
import { useMixpanel } from '../../../../hooks/useMixpanel'
import useUser from '../../../../hooks/useUser'
import { TheBigPOSApi } from '../../../../lib/TheBigPOSClient'
import { navigationLinking } from '../../../../services/navigation'
import { ModalAutomatedServices } from '../../../../components/modals/ModalAutomatedServices'
import type { GenerateLoanDocumentFormFieldValues } from '../../../../forms/GenerateLoanDocumentForm'
import { getTheme } from '../../../../config'
import { useAlert, useSiteConfig } from '../../../../hooks'
import {
	LayoutRow,
	LayoutRowGrid,
} from '../../../../components/layout'
import LoanCalculatorResults from '../components/LoanCalculatorResults'
import LoanCalculatorSharedFields from '../components/LoanCalculatorSharedFields'
import { formStateErrorsToErrorList } from '../utils/helpers'
import { useLoanContext } from '../../../../contexts/LoanContext'
import { ModalPleaseWait } from '../../../../components/modals/ModalPleaseWait'
import queryKeys from '../../../../services/queryKeys'
import ModalGenerateLoanDocumentSelector from '../../../../components/modals/ModalGenerateLoanDocumentSelector'
import {
	yupCurrencyValidator,
	yupCreditScoreValidator,
	yupPercentageValidator,
	yupRateValidator,
	yupZipCodeValidator,
} from '../../../../services/yupValidation'

const theme = getTheme()

export const loanCalculatorFormSchema = yup
	.object()
	.shape({
		loanPurpose: yup.string().required(),
		loanType: yup.string().required(),
		propertyValue: yupCurrencyValidator('Property Value').required(),
		downPaymentAmount: yupCurrencyValidator('Down Payment Amount'),
		downPaymentPercent: yupPercentageValidator(
			'Down Payment Percent'
		),
		loanAmount: yupCurrencyValidator('Loan Amount').required(),
		zipCode: yupZipCodeValidator(),
		creditScore: yupCreditScoreValidator(),
		taxes: yupCurrencyValidator('Taxes'),
		insurance: yupCurrencyValidator('Insurance'),
		escrow: yup.string(),
		totalAssets: yupCurrencyValidator('Total Assets'),
		loanTerm1: yup.string().required(),
		loanTerm2: yup.string().required(),
		propertyOccupancy: yup.string(),
		propertyType: yup.string(),
		BorrowerIncome: yupCurrencyValidator('Borrower Income'),
		lienType: yup.string().required(),
		rate: yupRateValidator(),
		county: yup.string(),
		flood: yupCurrencyValidator('Flood Insurance'),
		hoa: yupCurrencyValidator('HOA'),
		miFactor: yupRateValidator('MI Factor'),
		mi: yupCurrencyValidator('MI'),
		preApprovalNotes: yup.string(),
		additionalPreApprovalNotes: yup.string().nullable(),
	})
	.required()

export const loanCalculatorFormDefaultValues: Omit<
	LoanCalculatorFormValues,
	'loanID'
> = {
	loanPurpose: LoanPurposeTypes.Purchase,
	loanType: '',
	propertyValue: '',
	downPaymentAmount: '',
	downPaymentPercent: '',
	loanAmount: '',
	zipCode: '',
	creditScore: '',
	taxes: '',
	insurance: '',
	escrow: '',
	totalAssets: '',
	loanTerm1: '',
	loanTerm2: '',
	propertyOccupancy: '',
	propertyType: '',
	BorrowerIncome: '',
	lienType: '',
	rate: '',
	county: '',
	flood: '',
	hoa: '',
	miFactor: '',
	mi: '',
	preApprovalNotes: '',
	additionalPreApprovalNotes: '',
}

export type LoanCalculatorFormProps = {
	loanId: string
	onSubmit?: (data: LoanCalculatorFormValues) => void
	loading?: boolean
	defaultValues: LoanCalculatorFormValues
	loanLocked?: boolean
}

const LoanCalculatorForm = ({
	loanId,
	onSubmit,
	loading = false,
	defaultValues,
	loanLocked = false,
}: LoanCalculatorFormProps) => {
	const { siteConfig } = useSiteConfig()
	const { isLoanAdmin } = useUser()
	const mixpanel = useMixpanel()
	const ventiState = useVenti()
	const navigate = useNavigate()
	const { loanData, loan } = useLoanContext()
	const { formatMessage } = useIntl()
	const { alert } = useAlert()

	const [isLoading, setIsLoading] = useState(false)
	const [loadingMessage, setLoadingMessage] = useState('')

	const [
		isAutomatedServicesModalOpen,
		setIsAutomatedServicesModalOpen,
	] = useState(false)
	const [
		isGenerateLoanDocumentModalOpen,
		setIsGenerateLoanDocumentModalOpen,
	] = useState(false)
	const [generateLoanDocumentType, setGenerateLoanDocumentType] =
		useState('')

	const getTaskDocumentsByLoan = useQuery({
		queryKey: [queryKeys.loanDocuments, loanId],
		queryFn: () => TheBigPOSApi.getTaskDocumentsByLoan(loanId),
		enabled: !!loanId && loanId !== '',
	})

	const createLegacyLoanDocument = useMutation({
		mutationFn: (data: {
			templateID: string
			recipients: string[]
		}) =>
			TheBigPOSApi.createLegacyLoanDocument({
				templateID: data.templateID,
				loanID: loanId,
				siteConfigurationID: siteConfig.id,
				preview: false,
				recipients: data.recipients,
			}),
	})

	const methods = useForm<LoanCalculatorFormValues>({
		schema: loanCalculatorFormSchema,
		defaultValues: loanCalculatorFormDefaultValues || defaultValues,
		values: defaultValues,
	})

	const {
		getValues,
		trigger,
		watch,
		formState: { errors },
	} = methods

	const loanPurpose = watch('loanPurpose')

	const handleGeneratePrequalDocumentModalOpen = () => {
		setGenerateLoanDocumentType('PrequalLetter')
		setIsGenerateLoanDocumentModalOpen(true)
	}

	const handleGenerateLoanDocumentModalClose = () => {
		setIsGenerateLoanDocumentModalOpen(false)
	}

	const handleGeneratePreapprovalDocumentModalOpen = () => {
		setGenerateLoanDocumentType('PreapprovalLetter')
		setIsGenerateLoanDocumentModalOpen(true)
	}

	const handleCalculationSubmit = async () => {
		const isValid = await trigger()

		if (!isValid) {
			return
		}

		const data = getValues()

		onSubmit?.(data)
	}

	const handleGenerateLetter = async (
		formData: GenerateLoanDocumentFormFieldValues
	) => {
		handleGenerateLoanDocumentModalClose()

		try {
			setIsLoading(true)

			setLoadingMessage(`Updating & Retrieving calculations`)

			await handleCalculationSubmit()

			setLoadingMessage(
				`Please be patient while the document is generated`
			)

			const createLegacyLoanDocumentResponse =
				await createLegacyLoanDocument.mutateAsync({
					templateID: formData.customDocumentTemplateId,
					recipients: formData.recipients,
				})

			const { data: letter } = createLegacyLoanDocumentResponse

			ventiState.set(theme.storageKeys.document, {
				...letter,
				extension: letter?.extension?.replace('.', ''),
			})

			setLoadingMessage(`Fetching loan documents`)

			const { data: getTaskDocumentByLoanResponse } =
				await getTaskDocumentsByLoan.refetch()

			setIsLoading(false)

			await ventiState.set(
				theme.storageKeys.loanDocs,
				getTaskDocumentByLoanResponse?.data
			)

			const navigatePath = generatePath(
				`/${navigationLinking.Documents}`,
				{ loanId }
			)
			navigate(navigatePath)
		} catch {
			setIsLoading(false)
			alert(formatMessage({ id: 'errors.general' }), {
				severity: 'error',
			})
		}
	}

	const handleOpenLOConnectServices = () => {
		window.open(
			`https://encompassloconnect.com/pipeline/${loanId.slice(1, -1)}/services`,
			'_blank',
			'noopener,noreferrer'
		)
		mixpanel.trackEvent(eventTypes.CREDIT_AUS_PRICING, {
			loanId,
		})
	}

	const handleOpenMobileServiceOrder = () => {
		setIsAutomatedServicesModalOpen(true)
	}

	const borrowers = useMemo(() => {
		return (loan?.userLoans || [])
			.map((x: UserLoan) => x.user)
			.filter(
				(x): x is User =>
					x !== null &&
					x !== undefined &&
					(x.role === 'Borrower' || x.role === 'CoBorrower')
			)
	}, [loan?.userLoans])

	const unfixableErrors = useMemo(() => {
		const disabledRequiredFields = [
			'loanPurpose',
			'loanType',
			'propertyValue',
			'loanTerm1',
			'loanTerm2',
			'lienType',
		]

		return (
			!isLoanAdmin &&
			formStateErrorsToErrorList(errors).some((error) =>
				disabledRequiredFields.includes(error.property)
			)
		)
	}, [isLoanAdmin, errors])

	return (
		<>
			<Form
				errors={formStateErrorsToErrorList(errors)}
				errorsTitle={
					unfixableErrors
						? formatMessage({
								id: 'errors.loan_calculator_permissions',
							})
						: undefined
				}
			>
				<FormProvider {...methods}>
					<LayoutRowGrid columns={2}>
						<LayoutRowGrid
							columns={2}
							className="border-neutral-200 md:border-r md:px-6"
						>
							<LoanCalculatorSharedFields loanLocked={loanLocked} />
							<FormRow className="md:col-span-2">
								<TextField
									name="preApprovalNotes"
									label="Pre-Approval Notes"
									disabled={loanLocked || !isLoanAdmin}
									multiline
									rows={3}
									inputProps={{ maxLength: 1000 }}
									fullWidth
								/>
							</FormRow>
							{isLoanAdmin && (
								<FormRow className="md:col-span-2">
									<TextField
										name="additionalPreApprovalNotes"
										label="Additional Pre-Approval Notes"
										disabled={loanLocked}
										multiline
										rows={3}
										inputProps={{ maxLength: 1000 }}
										fullWidth
									/>
								</FormRow>
							)}
						</LayoutRowGrid>
						<LayoutRow>
							<LoanCalculatorResults
								loanCalculatorData={defaultValues}
							/>
							<div className="flex flex-col items-center gap-3 mt-6">
								<LoadingBtn
									text={formatMessage({
										id: 'loan_calculator.submit',
									})}
									onClick={handleCalculationSubmit}
									loading={loading || isLoading}
									disabled={loanLocked}
									style={{
										width: 280,
									}}
								/>
								<hr className="w-full max-w-[320px] border-0 border-b border-neutral-200 my-3" />
								{isLoanAdmin &&
									siteConfig?.enabledServices?.encompassWeb && (
										<LoadingBtn
											text="Encompass Web Services"
											onClick={handleOpenLOConnectServices}
											loading={loading || isLoading}
											disabled={isLoading || loanLocked}
											variant="contained"
											style={{ width: 280 }}
										/>
									)}
								{isLoanAdmin && siteConfig?.asoSettings?.enabled && (
									<LoadingBtn
										id="LOCalculatorAutomatedServicesButton"
										text="Mobile Service Order"
										onClick={handleOpenMobileServiceOrder}
										loading={loading || isLoading}
										disabled={loanLocked}
										variant="contained"
										style={{ width: 280 }}
									/>
								)}
								{loanPurpose === LoanPurposeTypes.Purchase && (
									<>
										<Button
											id="LOCalculatorFormGeneratePrequalLetterButton"
											text="Generate Prequal Letter"
											onClick={handleGeneratePrequalDocumentModalOpen}
											disabled={
												!defaultValues?.canGeneratePreQual ||
												loanLocked
											}
											variant="contained"
											style={{ width: 280 }}
										/>
										<Button
											id="LOCalculatorFormGeneratePreapprovalLetterButton"
											text="Generate Preapproval Letter"
											onClick={
												handleGeneratePreapprovalDocumentModalOpen
											}
											disabled={
												!defaultValues?.canGeneratePreApproval ||
												loanLocked
											}
											variant="contained"
											style={{ width: 280 }}
										/>
									</>
								)}
							</div>
						</LayoutRow>
					</LayoutRowGrid>
				</FormProvider>
			</Form>
			<ModalGenerateLoanDocumentSelector
				open={isGenerateLoanDocumentModalOpen}
				onSubmit={handleGenerateLetter}
				borrowers={borrowers}
				onClose={handleGenerateLoanDocumentModalClose}
				type={generateLoanDocumentType}
				fullWidth
				title="Which Document Template would you like to apply?"
			/>
			<ModalAutomatedServices
				visible={isAutomatedServicesModalOpen}
				setVisible={setIsAutomatedServicesModalOpen}
				loanData={loanData}
			/>
			<ModalPleaseWait visible={isLoading} message={loadingMessage} />
		</>
	)
}

export default LoanCalculatorForm
